import { Blossom } from '@blossom-carousel/core';

const fetchCache = new Map();
const requestIdle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));

function prefetchOffScreenSliders() {
	const sliders = document.querySelectorAll('bundle-model-slider');
	for (const slider of sliders) {
		if (slider.loaded || slider.isVisible) continue;

		const url = slider.buildFetchUrl();
		if (!url || fetchCache.has(url)) continue;

		fetch(url, { priority: 'low' })
			.then((res) => (res.ok ? res.json() : null))
			.then((json) => {
				if (json) fetchCache.set(url, Object.values(json)[0]);
			})
			.catch(() => {});
	}
}

class BundleModelSlider extends HTMLElement {
	connectedCallback() {
		this.globalFilter = null;
		this.loaded = false;
		this.isVisible = false;
		this.controller = null;
		this.carousel = null;

		this.filterParam = this.dataset.filterParam;
		this.filterValue = this.dataset.filterValue;

		this.onFilterChanged = this.onFilterChanged.bind(this);
		this.onNavClick = this.onNavClick.bind(this);
		this.onPrefetchFilter = this.onPrefetchFilter.bind(this);
		document.addEventListener('bundle:filter-changed', this.onFilterChanged);
		document.addEventListener('bundle:prefetch-filter', this.onPrefetchFilter);

		this.onScroll = () => this.updateNavState();

		this.wrapper = this.closest('.bundle-model');
		this.navButtons = this.wrapper?.querySelectorAll('.bundle-model__nav-btn');
		this.navButtons?.forEach((btn) => btn.addEventListener('click', this.onNavClick));

		const filterNav = document.querySelector('bundle-filter-nav');
		if (filterNav?.activeValue) {
			const sectionId = filterNav.dataset.sectionId;
			const filters = window.BundleBuilder?.[sectionId]?.filters || [];
			const active = filters.find((f) => f.value === filterNav.activeValue);
			if (active) {
				this.globalFilter = { param: active.param, value: active.value };
			}
		}

		this.observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					this.isVisible = entry.isIntersecting;
					if (this.isVisible && !this.loaded) {
						this.fetchProducts();
					}
				}
			},
			{ rootMargin: '200px' }
		);
		this.observer.observe(this);
	}

	disconnectedCallback() {
		document.removeEventListener('bundle:filter-changed', this.onFilterChanged);
		document.removeEventListener('bundle:prefetch-filter', this.onPrefetchFilter);
		this.navButtons?.forEach((btn) => btn.removeEventListener('click', this.onNavClick));
		this.activeScroller?.removeEventListener('scroll', this.onScroll);
		this.observer?.disconnect();
		this.controller?.abort();
		this.carousel?.destroy();
	}

	onNavClick(e) {
		const dir = e.currentTarget.dataset.dir;
		const scroller = this.querySelector('.bundle-model-slider__scroller');
		if (!scroller) return;

		const items = [...scroller.children];
		if (items.length === 0) return;

		const currentIndex = this.getActiveIndex(scroller, items);
		const targetIndex =
			dir === 'next'
				? Math.min(currentIndex + 1, items.length - 1)
				: Math.max(currentIndex - 1, 0);

		items[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
	}

	getActiveIndex(scroller, items) {
		const scrollerLeft = scroller.getBoundingClientRect().left;
		let closest = 0;
		let minDist = Infinity;
		for (let i = 0; i < items.length; i++) {
			const dist = Math.abs(items[i].getBoundingClientRect().left - scrollerLeft);
			if (dist < minDist) {
				minDist = dist;
				closest = i;
			}
		}
		return closest;
	}

	updateNavState() {
		const scroller = this.querySelector('.bundle-model-slider__scroller');
		if (!scroller || !this.navButtons) return;

		const atStart = scroller.scrollLeft <= 1;
		const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;

		const prevBtn = this.wrapper?.querySelector('[data-dir="prev"]');
		const nextBtn = this.wrapper?.querySelector('[data-dir="next"]');

		if (prevBtn) prevBtn.disabled = atStart;
		if (nextBtn) nextBtn.disabled = atEnd;
	}

	onFilterChanged(event) {
		this.globalFilter = event.detail;
		this.loaded = false;

		if (this.isVisible) {
			this.fetchProducts();
		}
	}

	onPrefetchFilter(event) {
		const filter = event.detail;
		const url = this.buildFetchUrl(filter);
		if (!url || fetchCache.has(url)) return;

		fetch(url, { priority: 'low' })
			.then((res) => (res.ok ? res.json() : null))
			.then((json) => {
				if (json) fetchCache.set(url, Object.values(json)[0]);
			})
			.catch(() => {});
	}

	buildFetchUrl(filterOverride) {
		const builder = this.closest('bundle-builder');
		const collectionHandle = builder?.dataset.collection;
		if (!collectionHandle) return null;

		const sectionType = this.getSectionType();
		const params = new URLSearchParams();
		params.set('sections', sectionType);

		if (this.filterParam && this.filterValue) {
			params.set(this.filterParam, this.filterValue);
		}

		const filter = filterOverride || this.globalFilter;
		if (filter?.param && filter?.value) {
			params.set(filter.param, filter.value);
		}

		return `/collections/${collectionHandle}?${params.toString()}`;
	}

	async fetchProducts() {
		const url = this.buildFetchUrl();
		if (!url) return;

		this.classList.add('is-loading');
		this.showLoader();

		if (this.controller) this.controller.abort();
		this.controller = new AbortController();

		try {
			let html;

			if (fetchCache.has(url)) {
				html = fetchCache.get(url);
			} else {
				const priority = this.getSliderIndex() < 2 ? 'high' : 'low';
				const response = await fetch(url, { signal: this.controller.signal, priority });
				if (!response.ok) throw new Error(`HTTP ${response.status}`);

				const json = await response.json();
				html = Object.values(json)[0];
				fetchCache.set(url, html);
			}
			const doc = new DOMParser().parseFromString(html, 'text/html');

			const blockId = this.dataset.blockId;
			const sourceSlider = doc.querySelector(
				`bundle-model-slider[data-block-id="${blockId}"]`
			);

			const products = sourceSlider
				? sourceSlider.querySelectorAll('bundle-product')
				: [];

			const wrapper = this.closest('.bundle-model');

			if (products.length === 0) {
				if (wrapper) wrapper.hidden = true;
				this.hideLoader();
				this.classList.remove('is-loading');
				this.loaded = true;
				return;
			}

			if (wrapper) {
				wrapper.hidden = false;
				wrapper.classList.add('is-loaded');
			}

			if (this.carousel) {
				this.carousel.destroy();
				this.carousel = null;
			}

			const oldScroller = this.querySelector('.bundle-model-slider__scroller');
			if (oldScroller) oldScroller.remove();

			const scroller = document.createElement('div');
			scroller.className = 'bundle-model-slider__scroller';

			const isFirstSlider = this.getSliderIndex() === 0;
			for (let i = 0; i < products.length; i++) {
				const clone = products[i].cloneNode(true);
				if (isFirstSlider && i < 2) {
					const img = clone.querySelector('img');
					if (img) img.loading = 'eager';
				}
				scroller.appendChild(clone);
			}

			this.appendChild(scroller);
			this.hideLoader();
			this.initCarousel(scroller);
			this.loaded = true;
			requestIdle(() => prefetchOffScreenSliders());
		} catch (error) {
			if (error.name !== 'AbortError') {
				console.error('Bundle model slider fetch failed:', error);
			}
		} finally {
			this.classList.remove('is-loading');
		}
	}

	initCarousel(scroller) {
		this.activeScroller?.removeEventListener('scroll', this.onScroll);
		this.carousel = Blossom(scroller, {});
		this.carousel.init();
		this.activeScroller = scroller;
		scroller.addEventListener('scroll', this.onScroll, { passive: true });
		this.updateNavState();
	}

	showLoader() {
		let loader = this.querySelector('.bundle-model-slider__loader');
		if (!loader) {
			loader = document.createElement('div');
			loader.className = 'bundle-model-slider__loader';
			const spinner = document.createElement('div');
			spinner.className = 'bundle-model-slider__spinner';
			loader.appendChild(spinner);
			this.appendChild(loader);
		}
		loader.hidden = false;
	}

	hideLoader() {
		const loader = this.querySelector('.bundle-model-slider__loader');
		if (loader) loader.hidden = true;
	}

	getSliderIndex() {
		const builder = this.closest('bundle-builder');
		if (!builder) return 0;
		const sliders = builder.querySelectorAll('bundle-model-slider');
		return [...sliders].indexOf(this);
	}

	getSectionType() {
		const section = this.closest('[id^="shopify-section-"]');
		if (!section) return 'section-bundle-builder';
		return section.id.replace('shopify-section-', '');
	}
}

customElements.define('bundle-model-slider', BundleModelSlider);
